import React, { useState, useEffect } from "react";
import axios from "axios";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ExportIcon from "../../components/Images/icons/Export_Icon_01.png";
import { toast } from "react-toastify";
import Response_Loading_processing from "../../layout/Others/Response_Loading_processing";
import { getItemToken } from "../../Utils/localStorageUtils";
import { donwloadCSV, videodownloadPDF, videodownloadXLSX } from "../../components/CommonMethod/CommonMethods";
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from "react-icons/md";
import { RxReload } from "react-icons/rx";
import { useMyContext } from "../../components/Provider/CommonProvider";

function Partner_Content_List() {
  const [apiData, setApiData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [withOutRefresh, setWithoutRefresh] = useState(false);

  // Partner dropdown states
  const [partnerList, setPartnerList] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [paymentHistory, setPaymentHistory] = useState([]);

  const { fetchRequest } = useMyContext();
  const BaseUrl = process.env.REACT_APP_Baseurl;
  const listApi = `${BaseUrl}/admin/partner-monitazation-listAllContents`;

  // Fetch partner list for dropdown
  const fetchPartnerList = async () => {
    try {
      const response = await fetchRequest({
        method: "GET",
        url: `${BaseUrl}/admin/partner-monitazation-index`,
        data: {},
      });

      if (response?.status) {
        setPartnerList(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchRequest({
        method: "GET",
        url: listApi,
        data: {},
      });

      if (response?.status) {
        setApiData(response?.data || []);
        setFilteredData(response?.data || []);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch data");
      setLoading(false);
    }
  };

  // Fetch payment history for selected partner
  const fetchPaymentHistory = async (partnerId) => {
    if (!partnerId) {
      setPaymentHistory([]);
      return;
    }

    setLoading(true);
    try {
      // This is a placeholder - replace with actual payment history API endpoint
      const response = await fetchRequest({
        method: "GET",
        url: `${BaseUrl}/admin/partner-payment-history?partner_id=${partnerId}`,
        data: {},
      });

      if (response?.status) {
        setPaymentHistory(response?.data || []);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching payment history:", error);
      setPaymentHistory([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartnerList();
    fetchData();
  }, [withOutRefresh]);

  // Handle partner selection
  const handlePartnerChange = (event) => {
    const partnerId = event.target.value;
    setSelectedPartner(partnerId);
    fetchPaymentHistory(partnerId);
  };

  const handleFilterChange = (event) => {
    const searchTerm = event.target.value.toLowerCase();
    setFilter(searchTerm);

    // Filter based on current view (payment history or content list)
    if (selectedPartner && paymentHistory.length > 0) {
      // Filter payment history
      const filteredResults = paymentHistory.filter((item) => {
        return (
          (item?.month_year?.toLowerCase()?.includes(searchTerm)) ||
          (item?.paid_amount?.toString()?.toLowerCase()?.includes(searchTerm)) ||
          (item?.balance_amount?.toString()?.toLowerCase()?.includes(searchTerm)) ||
          (item?.transaction_id?.toLowerCase()?.includes(searchTerm)) ||
          (item?.payment_method?.toLowerCase()?.includes(searchTerm))
        );
      });
      setPaymentHistory(filteredResults);
    } else {
      // Filter content list
      const filteredResults = apiData.filter((item) => {
        return (
          (item?.id?.toString()?.toLowerCase()?.includes(searchTerm)) ||
          (item?.title?.toLowerCase()?.includes(searchTerm)) ||
          (item?.partner?.name?.toLowerCase()?.includes(searchTerm)) ||
          (item?.source_type?.toString()?.toLowerCase()?.includes(searchTerm)) ||
          (item?.total_views?.toString()?.toLowerCase()?.includes(searchTerm)) ||
          (item?.monetization_amount?.toString()?.toLowerCase()?.includes(searchTerm)) ||
          (item?.admin_commission?.toString()?.toLowerCase()?.includes(searchTerm)) ||
          (item?.partner_commission?.toString()?.toLowerCase()?.includes(searchTerm))
        );
      });
      setFilteredData(filteredResults);
    }
  };

  const handleClear = () => {
    setFilter("");
    if (selectedPartner) {
      // Reload payment history without filter
      fetchPaymentHistory(selectedPartner);
    } else {
      setFilteredData(apiData);
    }
  };

  const reloadAction = () => {
    setWithoutRefresh(prev => !prev);
  };

  // Helper function to get content type name
  const getContentType = (sourceType) => {
    switch (sourceType) {
      case 1: return 'Video';
      case 2: return 'Series';
      case 3: return 'Live Stream';
      case 4: return 'Audio';
      default: return 'Unknown';
    }
  };

  // Export Functions
  const fetchAllData = async (type, find) => {
    let exportData;
    let filename;
    let headers;

    if (selectedPartner) {
      // Export payment history
      const updatedList = find === "ALL" ? paymentHistory : paymentHistory;
      exportData = updatedList.map((item, index) => ({
        "S.NO": index + 1,
        "MONTH/YEAR": item?.month_year || 'N/A',
        "PAID AMOUNT": item?.paid_amount || 0,
        "BALANCE AMOUNT": item?.balance_amount || 0,
        "TRANSACTION ID": item?.transaction_id || 'N/A',
        "PAYMENT METHOD": item?.payment_method || 'N/A',
      }));
      filename = "PARTNER-PAYMENT-HISTORY";
      headers = ["S.NO", "MONTH/YEAR", "PAID AMOUNT", "BALANCE AMOUNT", "TRANSACTION ID", "PAYMENT METHOD"];
    } else {
      // Export content list
      const updatedList = find === "ALL" ? apiData : filteredData;
      exportData = updatedList.map((item, index) => ({
        "S.NO": index + 1,
        "ID": item?.id,
        "CONTENT TITLE": item?.title,
        "PARTNER NAME": item?.partner?.name || 'N/A',
        "CONTENT TYPE": getContentType(item?.source_type),
        "TOTAL VIEWS": item?.total_views,
        "MONETIZED AMOUNT": `$${parseFloat(item?.monetization_amount || 0).toFixed(2)}`,
        "ADMIN COMMISSION": `$${parseFloat(item?.admin_commission || 0).toFixed(2)}`,
        "PARTNER COMMISSION": `$${parseFloat(item?.partner_commission || 0).toFixed(2)}`,
      }));
      filename = "PARTNER-CONTENT-LIST";
      headers = [
        "S.NO", "ID", "CONTENT TITLE", "PARTNER NAME", "CONTENT TYPE", "TOTAL VIEWS",
        "MONETIZED AMOUNT", "ADMIN COMMISSION", "PARTNER COMMISSION"
      ];
    }

    if (type === "CSV") {
      await donwloadCSV(exportData, filename);
    } else if (type === "PDF") {
      await videodownloadPDF(exportData, filename, headers);
    } else if (type === "XLSX") {
      await videodownloadXLSX(exportData, filename, headers);
    }
  };

  // Payment History Columns - for when partner is selected
  const paymentHistoryColumns = [
    {
      dataField: "month_year",
      text: "Month / Year",
      sort: true,
      headerStyle: { width: '150px', minWidth: '150px' },
      style: { width: '150px', minWidth: '150px' },
      formatter: (cell, row) => {
        // Format as "MM/YYYY" or use the value as-is
        return cell || 'N/A';
      }
    },
    {
      dataField: "paid_amount",
      text: "Paid Amount",
      sort: true,
      headerStyle: { width: '150px', minWidth: '150px' },
      style: { width: '150px', minWidth: '150px' },
      formatter: (cell) => cell ? `$${parseFloat(cell).toFixed(2)}` : '$0.00'
    },
    {
      dataField: "balance_amount",
      text: "Balance Amount",
      sort: true,
      headerStyle: { width: '160px', minWidth: '160px' },
      style: { width: '160px', minWidth: '160px' },
      formatter: (cell) => cell ? `$${parseFloat(cell).toFixed(2)}` : '$0.00'
    },
    {
      dataField: "transaction_id",
      text: "Transaction ID",
      sort: true,
      headerStyle: { width: '180px', minWidth: '180px' },
      style: { width: '180px', minWidth: '180px' },
      formatter: (cell) => cell || 'N/A'
    },
    {
      dataField: "payment_method",
      text: "Payment Method",
      sort: true,
      headerStyle: { width: '160px', minWidth: '160px' },
      style: { width: '160px', minWidth: '160px' },
      formatter: (cell) => cell || 'N/A'
    },
  ];

  // Content List Columns - default view
  const columns = [
    {
      dataField: "rowNumber",
      text: "Id",
      sort: false,
      headerStyle: { width: '80px', minWidth: '80px' },
      style: { width: '80px', minWidth: '80px' },
      formatter: (cell, row, rowIndex) => rowIndex + 1
    },
    {
      dataField: "title",
      text: "Content Title",
      sort: true,
      headerStyle: { width: '200px', minWidth: '200px' },
      style: { width: '200px', minWidth: '200px' }
    },
    {
      dataField: "partner.name",
      text: "Partner Name",
      sort: true,
      headerStyle: { width: '150px', minWidth: '150px' },
      style: { width: '150px', minWidth: '150px' },
      formatter: (cell, row) => row?.partner?.name || 'N/A'
    },
    {
      dataField: "source_type",
      text: "Content Type",
      sort: true,
      headerStyle: { width: '130px', minWidth: '130px' },
      style: { width: '130px', minWidth: '130px' },
      formatter: (cell) => getContentType(cell)
    },
    {
      dataField: "total_views",
      text: "Views",
      sort: true,
      headerStyle: { width: '100px', minWidth: '100px' },
      style: { width: '100px', minWidth: '100px' }
    },
    {
      dataField: "monetization_amount",
      text: "Monetized Amount",
      sort: true,
      headerStyle: { width: '160px', minWidth: '160px' },
      style: { width: '160px', minWidth: '160px' },
      formatter: (cell) => cell ? `$${parseFloat(cell).toFixed(2)}` : '$0.00'
    },
    {
      dataField: "admin_commission",
      text: "Admin Commission",
      sort: true,
      headerStyle: { width: '180px', minWidth: '180px' },
      style: { width: '180px', minWidth: '180px' },
      formatter: (cell) => cell ? `$${parseFloat(cell).toFixed(2)}` : '$0.00'
    },
    {
      dataField: "partner_commission",
      text: "Partner Commission",
      sort: true,
      headerStyle: { width: '180px', minWidth: '180px' },
      style: { width: '180px', minWidth: '180px' },
      formatter: (cell) => cell ? `$${parseFloat(cell).toFixed(2)}` : '$0.00'
    },
  ];

  // Pagination Config for Payment History
  const paymentHistoryPaginationConfig = paginationFactory({
    page: 1,
    sizePerPage: 10,
    nextPageText: "Next",
    prePageText: "Prev",
    firstPageText: <MdKeyboardDoubleArrowLeft />,
    lastPageText: <MdKeyboardDoubleArrowRight />,
    showTotal: true,
    alwaysShowAllBtns: true,
    sizePerPageList: [
      { text: "5", value: 5 },
      { text: "10", value: 10 },
      { text: "All", value: paymentHistory?.length },
    ],
  });

  // Pagination Config for Content List
  const paginationConfig = paginationFactory({
    page: 1,
    sizePerPage: 10,
    nextPageText: "Next",
    prePageText: "Prev",
    firstPageText: <MdKeyboardDoubleArrowLeft />,
    lastPageText: <MdKeyboardDoubleArrowRight />,
    showTotal: true,
    alwaysShowAllBtns: true,
    sizePerPageList: [
      { text: "5", value: 5 },
      { text: "10", value: 10 },
      { text: "All", value: filteredData?.length },
    ],
  });

  return (
    <>
      <section className="section container-fluid pt-3">
        <div className="row">
          <div className="col-sm-12">
            <div className="iq-card">
              <div className="iq-card-header d-flex justify-content-between align-items-center flex-wrap">
                <div className="iq-header-title">
                  <h4 className="card-title">
                    {selectedPartner ? 'Partner Payment History' : 'Partner Content List'}
                  </h4>
                </div>
                {/* <div className="d-flex align-items-center gap-2">
                  <select 
                    className="form-select" 
                    style={{ minWidth: '250px' }}
                    value={selectedPartner}
                    onChange={handlePartnerChange}
                  >
                    <option value="">-- Select Channel --</option>
                    {partnerList.map((partner) => (
                      <option key={partner.partner_id} value={partner.partner_id}>
                        {partner?.user?.name || `Partner ${partner.partner_id}`}
                      </option>
                    ))}
                  </select>
                </div> */}
              </div>
              <hr />
              <div className="iq-card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 gap-3">
                  <div className="filterContainer">
                    <input
                      type="text"
                      placeholder="Filter all.."
                      className="filter form-control"
                      value={filter}
                      onChange={handleFilterChange}
                    />
                    {filter?.length > 0 && (
                      <button type="button" className="closeBtn" onClick={handleClear}>
                        <i className="fa fa-times"></i>
                      </button>
                    )}
                  </div>

                  <div className="btn-group flex-wrap justify-content-between justify-content-sm-end">
                    <button
                      className="bg-transparent reloadButton theme-text-color p-2 me-2 border border-2 rounded-circle theme-border-color"
                      onClick={reloadAction}
                    >
                      <RxReload style={{ width: "18px", height: "15px" }} className={`${loading ? "rotate-load" : ""}`} />
                    </button>

                    <div className="dropdown d-block mb-2 mb-sm-0 mx-0" key={"2"}>
                      <button
                        className="btn btn-success dropdown-toggle d-flex mx-0"
                        type="button"
                        id="dropdownMenuButton1"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <span className="fw-bolder">Export </span>
                        <img src={ExportIcon} className="ms-2 exportIcon" alt="exportIcon" />
                      </button>
                      <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                        <li className="dropdown-item" onClick={() => fetchAllData("CSV")}>
                          CSV
                        </li>
                        <li className="dropdown-item" onClick={() => fetchAllData("PDF")}>
                          PDF
                        </li>
                        <li className="dropdown-item" onClick={() => fetchAllData("XLSX")}>
                          XLSX
                        </li>
                      </ul>
                    </div>

                    <div className="dropdown d-block">
                      <button
                        className="btn btn-success dropdown-toggle d-flex"
                        type="button"
                        id="dropdownMenuButton2"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <span className="fw-bolder">Export All</span>
                        <img src={ExportIcon} className="ms-2 exportIcon" alt="exportAllIcon" />
                      </button>
                      <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton2">
                        <li className="dropdown-item" onClick={() => fetchAllData("CSV", "ALL")}>
                          CSV
                        </li>
                        <li className="dropdown-item" onClick={() => fetchAllData("PDF", "ALL")}>
                          PDF
                        </li>
                        <li className="dropdown-item" onClick={() => fetchAllData("XLSX", "ALL")}>
                          XLSX
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="table-responsive" style={{ overflowX: 'auto', width: '100%' }}>
                  <div className="bootstrapTable" style={{ minWidth: selectedPartner ? '800px' : '1200px' }}>
                    {selectedPartner ? (
                      // Show Payment History Table when partner is selected
                      <BootstrapTable
                        keyField="id"
                        columns={paymentHistoryColumns}
                        data={paymentHistory}
                        pagination={paymentHistoryPaginationConfig}
                        className="no-border-table"
                        noDataIndication={
                          <div className="noRecord">
                            {loading ? (
                              <label>
                                <Response_Loading_processing />
                                Loading payment history...
                              </label>
                            ) : (
                              <p>No payment history found for this partner!</p>
                            )}
                          </div>
                        }
                        hover
                      />
                    ) : (
                      // Show Content List Table when no partner selected
                      <BootstrapTable
                        keyField="id"
                        columns={columns}
                        data={filteredData}
                        pagination={paginationConfig}
                        className="no-border-table"
                        noDataIndication={
                          <div className="noRecord">
                            {loading ? (
                              <label>
                                <Response_Loading_processing />
                                Loading...
                              </label>
                            ) : (
                              <p>No Data Found! Please select a channel above to view payment history.</p>
                            )}
                          </div>
                        }
                        hover
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Partner_Content_List;
